import * as React from "react"
import {useEffect, useState} from "react"
import {Slot} from "@radix-ui/react-slot"
import {cva, type VariantProps} from "class-variance-authority"

import {cn} from "components/ui/lib/utils"
import {usePermissionsStore} from "../../store/permissionsStore";
import {Tooltip, TooltipContent, TooltipTrigger} from "./tooltip";

const buttonVariants = cva(
    "inline-flex border-buttonborder hover:text-textlight items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
    {
        variants: {
            variant: {
                default:
                    "bg-button text-primary-foreground shadow",
                destructive:
                    "bg-destructive text-destructive-foreground shadow-sm ",
                outline:
                    "border hover:bg-buttonhover rounded border-input shadow-sm",
                secondary:
                    "bg-secondary text-secondary-foreground shadow-sm",
                ghost: "",
                link: "text-primary underline-offset-4 hover:underline",
            },
            size: {
                default: "h-9 px-4 py-2",
                sm: "h-8 rounded px-3 text-xs",
                lg: "h-10 rounded px-8",
                icon: "h-6 w-6",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
)

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean
    requiredPermission?: {
        operation: string
        resource: string
    }
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({className, variant, size, asChild = false, ...props}, ref) => {
        const [canUse, setCanUse] = useState<boolean>(true)
        const {hasPermissions} = usePermissionsStore()

        useEffect(() => {
            if (props.requiredPermission) {
                hasPermissions(props.requiredPermission.resource, props.requiredPermission.operation).then(x => {
                    setCanUse(x)
                })
            }
        }, [props.requiredPermission]);

        const Comp = asChild ? Slot : "button"
        return (
            canUse ?
                <Comp
                    className={cn(buttonVariants({variant, size, className}))}
                    ref={ref}
                    {...props}
                /> :
                <Tooltip>
                    <TooltipTrigger>
                        <Comp
                            className={cn(buttonVariants({variant, size, className}))}
                            ref={ref}
                            {...props}
                            disabled={true}
                        />
                    </TooltipTrigger>
                    <TooltipContent>
                        You do not have permission to use this. You need the <code>{props.requiredPermission?.operation}</code> operation on the <code>{props.requiredPermission?.resource}</code> resource. Ask an admin to grant this if you believe you should have it.
                    </TooltipContent>
                </Tooltip>
        )
    }
)
Button.displayName = "Button"

export {Button, buttonVariants}
